import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import "../css/CategoryList.css";

const SupplierPayment = () => {
  const { id } = useParams();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplierName, setSupplierName] = useState(location.state?.supplierName || "");

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [paymentType, setPaymentType] = useState("Cash"); // NEW: payment type state
  const [toast, setToast] = useState({ show: false, message: "", type: "" }); // Toast state

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  useEffect(() => {
    const fetchPayments = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}update-payment.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id }),
        });
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          setPayments(data.data);
        } else {
          setPayments([]);
        }
      } catch (error) {
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, [id]);

  // Reset to page 1 when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const filteredPayments = payments.filter((payment) => {
    const term = searchTerm.toLowerCase();
    return (
      payment.invoice_no.toLowerCase().includes(term) ||
      payment.purchase_date.toLowerCase().includes(term) ||
      payment.total_amt.toString().includes(term) ||
      payment.due_amount.toString().includes(term)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Modal handlers
  const handlePayClick = (payment) => {
    setSelectedPayment(payment);
    setPayAmount(payment.due_amount); // default to due amount
    setPaymentType("Cash"); // reset to default
    setShowModal(true);
  };

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  const handlePaymentSubmit = async () => {
    if (!payAmount || isNaN(payAmount) || Number(payAmount) <= 0) {
      showToast("Please enter a valid amount.", "error");
      return;
    }
    if (!paymentType) {
      showToast("Please select a payment type.", "error");
      return;
    }
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}make-payment.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          purchase_id: selectedPayment.purchase_id,
          paid_amount: payAmount,
          payment_type: paymentType,
        }),
      });
      const data = await response.json();
      if (data.status) {
        showToast("Payment successful!", "success");
        setShowModal(false);
        setTimeout(() => window.location.reload(), 1200); // Give user time to see toast
      } else {
        showToast(data.message || "Payment failed.", "error");
      }
    } catch (error) {
      showToast("Network error. Please try again.", "error");
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedPayment(null);
    setPayAmount("");
  };

  return (
    <div className="category-list-content">
      <h1 className="category-list-title pb-4">Supplier Payment</h1>
      {supplierName && (
        <div className="supplier-name" style={{ marginBottom: "1rem", fontWeight: "bold" }}>
          Supplier: {supplierName}
        </div>
      )}
      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by Invoice No, Date, Total, Due..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Invoice No</th>
              <th>Purchase Date</th>
              <th>Product Name</th>
              <th>Per Unit Price</th>
              <th>Quantity</th>
              <th>Total Amount</th>
              <th>Paid Amount</th>
              <th>Due Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center" }}>Loading...</td>
              </tr>
            ) : paginatedPayments.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ textAlign: "center" }}>No payments found.</td>
              </tr>
            ) : (
              paginatedPayments.map((payment) => (
                <tr key={payment.purchase_id}>
                  <td>{payment.invoice_no}</td>
                  <td>{payment.purchase_date}</td>
                  <td>{payment.product_name}</td>
                  <td>{payment.per_unit_price}</td>
                  <td>{payment.quantity} {payment.unit_name}</td>
                  <td>{payment.total_amt}</td>
                  <td>{payment.paid_amount}</td>
                  <td>{payment.due_amount}</td>
                  <td>
                    {Number(payment.due_amount) === 0 ? (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        background: '#4caf50',
                        color: 'white',
                        borderRadius: '12px',
                        fontSize: '0.9em',
                        fontWeight: 'bold'
                      }}>
                        Payment Complete
                      </span>
                    ) : (
                      <button className="action-btn payment" onClick={() => handlePayClick(payment)}>
                        Pay
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination" style={{ margin: '20px 0', textAlign: 'center' }}>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{ marginRight: '8px' }}
          >
            Prev
          </button>
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              onClick={() => setCurrentPage(i + 1)}
              className={currentPage === i + 1 ? "active" : ""}
              style={{
                fontWeight: currentPage === i + 1 ? 'bold' : 'normal',
                marginRight: '4px',
                background: currentPage === i + 1 ? '#1976d2' : '#fff',
                color: currentPage === i + 1 ? '#fff' : '#1976d2',
                border: '1px solid #1976d2',
                borderRadius: '4px',
                padding: '4px 10px',
                cursor: 'pointer',
              }}
            >
              {i + 1}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={{ marginLeft: '8px' }}
          >
            Next
          </button>
        </div>
      )}
      {/* Modal for payment */}
      {showModal && selectedPayment && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Pay Invoice</h3>
            <div className="modal-row">
              <span><b>Invoice No:</b> {selectedPayment.invoice_no}</span>
              <span><b>Due Amount:</b> {selectedPayment.due_amount}</span>
            </div>
            <label className="modal-label">
              Enter Paid Amount:
              <input
                type="number"
                className="modal-input"
                value={payAmount}
                min="1"
                max={selectedPayment.due_amount}
                onChange={e => {
                  let value = e.target.value;
                  if (Number(value) > Number(selectedPayment.due_amount)) {
                    value = selectedPayment.due_amount;
                  }
                  setPayAmount(value);
                }}
              />
            </label>
            <label className="modal-label" style={{ marginTop: "1rem" }}>
              Payment Type:
              <select
                className="modal-input"
                value={paymentType}
                onChange={e => setPaymentType(e.target.value)}
              >
                <option value="Cash">Cash</option>
                <option value="UPI">UPI</option>
                <option value="Card">Card</option>
              </select>
            </label>
            <div style={{ marginTop: "1rem" }}>
              <button onClick={handlePaymentSubmit} className="action-btn payment">Submit</button>
              <button onClick={handleModalClose} className="action-btn delete" style={{ marginLeft: "10px" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {/* Toast Notification */}
      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button
              className="toast-close"
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierPayment; 